import { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  TextField as MuiTextField,
  Typography,
  useTheme,
} from "@mui/material";
import { debounce } from "lodash-es";
import { useNavigate } from "react-router-dom";

import {
  canManageCareHomeRegister,
  canViewCareHomeRegister,
} from "@thalamos/aspire-common";

import { CareHomeRegister } from "./CareHomeRegister.js";
import { apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
} from "~/components/design-system/index.js";
import { MultiSelectFilterPicker } from "~/components/form/MultiSelectFilterPicker.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";

export function CareHomeRegisterPage(_props: Record<string, never>) {
  const theme = useTheme();
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;

  const [careHomeIds, setCareHomeIds] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");

  // Debounced because reading the register is an audited access of every resident it
  // returns, so a request per keystroke would fill the audit trail with reads nobody made
  // — as well as costing a round trip each time. Matches the MHA dashboard's 500ms.
  const applyFilter = useCallback(debounce(setAppliedFilter, 500), []);
  useEffect(() => {
    applyFilter(searchFilter);
  }, [searchFilter, applyFilter]);

  const [{ data, loading, error }] = apiHooks.capacityState.register(
    careHomeIds.length > 0 ? careHomeIds : undefined,
    appliedFilter || undefined,
  );

  // Both limbs are required: the session decides whether this user may curate at all, and
  // the grants decide whether there is any home for them to curate. A team with only
  // sight of its homes has nowhere to put a new resident, so it is not offered the option.
  const canManage =
    !!canManageCareHomeRegister(user) &&
    (data?.careHomes ?? []).some((home) => home.canManageRegister);

  if (!canViewCareHomeRegister(user)) {
    return (
      <Banner
        bannerType={BannerList.ERROR}
        title="You do not have access to care home registers"
      />
    );
  }

  const careHomeOptions = (data?.careHomes ?? []).map((home) => ({
    id: home.id,
    name: home.name,
  }));

  return (
    <Box sx={{ padding: "1em 1.5em" }}>
      <Box
        sx={{
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          paddingLeft: "0.8em",
          marginBottom: "1em",
        }}
      >
        <Typography component="h1" sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
          Residents
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "#54596B" }}>
          {`${user?.sessionContext?.organisationName ?? ""} — ${careHomeOptions.length} care home registers · capacity and deprivation of liberty`}
        </Typography>
      </Box>

      {/* Both controls use MUI's floating label so they share a baseline and a
          height — the design-system TextField stacks its label above, which left the
          two sitting at different heights in the same row. */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <Box sx={{ minWidth: "260px", flex: "0 1 320px" }}>
          <MultiSelectFilterPicker
            label="Filter by care home"
            options={careHomeOptions}
            value={careHomeOptions.filter((home) =>
              careHomeIds.includes(home.id),
            )}
            onChange={(homes) => {
              setCareHomeIds(homes.map((home) => home.id));
            }}
            testId="careHomeFilterPicker"
          />
        </Box>
        <Box sx={{ minWidth: "260px", flex: "0 1 320px" }}>
          <MuiTextField
            fullWidth
            name="residentSearchFilter"
            label="Filter by resident"
            placeholder="Name or NHS number"
            value={searchFilter}
            onChange={(event) => {
              setSearchFilter(event.target.value);
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {canManage && (
          <Button
            label="Add resident"
            // Adding somebody is patient search: it owns the local index, PDS, access
            // reasons and creating from a match. It lands on the resident page, which is
            // where the residency itself is established.
            onClick={() => {
              navigate(routeFns.patientSearch());
            }}
          />
        )}
      </Box>

      <CareHomeRegister
        data={data}
        isLoading={loading}
        error={error ? "Could not load the register" : undefined}
        reviewIntervalMonths={
          user?.sessionOrganisationConfiguration
            ?.mcaCapacityReviewIntervalMonths
        }
      />
    </Box>
  );
}
